import { ref, onMounted, onUnmounted } from 'vue';

/**
 * CSP-Compliant Styles Composable
 * 
 * Provides methods to add dynamic styles that respect Content Security Policy
 * by using nonces and avoiding inline styles where possible.
 */
export function useCSPCompliantStyles() {
  const styleElements = ref(new Map());
  const nonce = ref(null);

  /**
   * Get the CSP nonce from the window object or meta tag
   */
  const getCSPNonce = () => {
    // Try to get nonce from window object (set by middleware)
    if (window.cspNonce) {
      return window.cspNonce;
    }

    // Try to get nonce from meta tag
    const metaTag = document.querySelector('meta[name="csp-nonce"]');
    if (metaTag) {
      return metaTag.getAttribute('content');
    }

    // Try to get from request headers (if available)
    if (window.Laravel && window.Laravel.cspNonce) {
      return window.Laravel.cspNonce;
    }

    return null;
  };

  /**
   * Add a style element with CSP nonce
   */
  const addStyleElement = (id, css, options = {}) => {
    // Remove existing style element if it exists
    removeStyleElement(id);

    const style = document.createElement('style');
    style.id = id;
    style.textContent = css;

    // Add nonce if available
    const currentNonce = nonce.value || getCSPNonce();
    if (currentNonce) {
      style.setAttribute('nonce', currentNonce);
    }

    // Add other attributes
    if (options.media) {
      style.setAttribute('media', options.media);
    }

    if (options.type) {
      style.setAttribute('type', options.type);
    } else {
      style.setAttribute('type', 'text/css');
    }

    // Insert into head
    const insertionPoint = options.insertBefore 
      ? document.querySelector(options.insertBefore)
      : null;

    if (insertionPoint) {
      document.head.insertBefore(style, insertionPoint);
    } else {
      document.head.appendChild(style);
    }

    // Track the element
    styleElements.value.set(id, style);

    return style;
  };

  /**
   * Remove a style element
   */
  const removeStyleElement = (id) => {
    const existingStyle = styleElements.value.get(id);
    if (existingStyle && existingStyle.parentNode) {
      existingStyle.parentNode.removeChild(existingStyle);
      styleElements.value.delete(id);
    }
  };

  /**
   * Update an existing style element
   */
  const updateStyleElement = (id, css) => {
    const existingStyle = styleElements.value.get(id);
    if (existingStyle) {
      existingStyle.textContent = css;
    } else {
      addStyleElement(id, css);
    }
  };

  /**
   * Add CSS classes instead of inline styles
   */
  const addCSSClass = (className, css, options = {}) => {
    const wrappedCSS = `.${className} { ${css} }`;
    return addStyleElement(`class-${className}`, wrappedCSS, options);
  };

  /**
   * Apply styles to an element using CSS classes instead of inline styles
   */
  const applyStylesToElement = (element, styles, className = null) => {
    if (!element) return null;

    // Generate a unique class name if not provided
    const generatedClassName = className || `dynamic-style-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Convert styles object to CSS string
    let cssString = '';
    if (typeof styles === 'object') {
      cssString = Object.entries(styles)
        .map(([property, value]) => {
          // Convert camelCase to kebab-case
          const kebabProperty = property.replace(/([A-Z])/g, '-$1').toLowerCase();
          return `${kebabProperty}: ${value}`;
        })
        .join('; ');
    } else {
      cssString = styles;
    }

    // Add the CSS class
    addCSSClass(generatedClassName, cssString);

    // Apply the class to the element
    element.classList.add(generatedClassName);

    return generatedClassName;
  };

  /**
   * Create animation keyframes with CSP compliance
   */
  const addKeyframes = (name, keyframes, options = {}) => {
    let css = `@keyframes ${name} {`;
    
    Object.entries(keyframes).forEach(([percentage, styles]) => {
      css += `${percentage} {`;
      if (typeof styles === 'object') {
        Object.entries(styles).forEach(([property, value]) => {
          const kebabProperty = property.replace(/([A-Z])/g, '-$1').toLowerCase();
          css += `${kebabProperty}: ${value};`;
        });
      } else {
        css += styles;
      }
      css += '}';
    });
    
    css += '}';

    return addStyleElement(`keyframes-${name}`, css, options);
  };

  /**
   * Add media query styles
   */
  const addMediaQuery = (query, css, id = null) => {
    const mediaCSS = `@media ${query} { ${css} }`;
    const elementId = id || `media-${query.replace(/[^a-zA-Z0-9]/g, '-')}`;
    return addStyleElement(elementId, mediaCSS);
  };

  /**
   * Clean up all style elements
   */
  const cleanup = () => {
    styleElements.value.forEach((element, id) => {
      removeStyleElement(id);
    });
    styleElements.value.clear();
  };

  /**
   * Initialize nonce on mount
   */
  onMounted(() => {
    nonce.value = getCSPNonce();
    
    // Log CSP compliance status
    if (nonce.value) {
      console.log('CSP: Using nonce for dynamic styles:', nonce.value.substring(0, 8) + '...');
    } else {
      console.warn('CSP: No nonce found - dynamic styles may be blocked by CSP');
    }
  });

  /**
   * Clean up on unmount
   */
  onUnmounted(() => {
    cleanup();
  });

  return {
    // State
    nonce,
    styleElements,

    // Methods
    addStyleElement,
    removeStyleElement,
    updateStyleElement,
    addCSSClass,
    applyStylesToElement,
    addKeyframes,
    addMediaQuery,
    cleanup,
    getCSPNonce
  };
} 